// =====================================================
// CHAT API
// =====================================================

import { z } from 'zod';
import { apiFetch } from './api.core';
import {
  ConversationSchema,
  MessageSchema,
  AttachmentSchema,
  ReadReceiptSummarySchema,
} from '@resida/api-types/landlord-chat';
import { readFileIntoMemory } from '@resida/shared';

export interface ChatParticipant {
  id: string;
  identityId: string;
  participantType: 'TENANT' | 'MANAGER';
  displayName: string;
}

// Derived from ConversationSchema in @resida/api-types/landlord-chat
export type ChatConversation = z.infer<typeof ConversationSchema>;

// Derived from MessageSchema in @resida/api-types/landlord-chat
export type ChatMessage = z.infer<typeof MessageSchema>;

// Derived from AttachmentSchema in @resida/api-types/landlord-chat
export type ChatAttachment = z.infer<typeof AttachmentSchema>;

export interface CreateConversationInput {
  participantIdentityIds: string[];
  unitId?: string;
  propertyId?: string;
  portfolioId?: string;
  /**
   * TG-3/TG-13: when true, the unified conversation-creation endpoint atomically also
   * provisions the unit's Telegram GROUP chat and mints every participant an invite.
   * Mirrors CreateConversationWithTelegramRequestSchema's `connectTelegram` field
   * (apps/api/src/routes/manager/chat.routes.ts) — defaults to false server-side.
   */
  connectTelegram?: boolean;
}

export interface CreateMessageInput {
  content: string;
  attachmentIds?: string[];
}

export interface MarkReadInput {
  lastReadMessageId: string;
}

// Derived from ReadReceiptSummarySchema in @resida/api-types/landlord-chat
// TODO(api-types-922): see the ChatConversation TODO above — `readAt` is likewise typed `Date`
// but is an ISO string at runtime.
export type ChatReadReceipt = z.infer<typeof ReadReceiptSummarySchema>;

export interface ConversationListParams {
  unitId?: string;
  propertyId?: string;
  portfolioId?: string;
  participantIdentityId?: string;
}

export interface MessageListParams {
  cursor?: string;
  limit?: number;
}

/**
 * List conversations
 */
export async function listConversations(params?: ConversationListParams): Promise<{ conversations: ChatConversation[] }> {
  const searchParams = new URLSearchParams();
  if (params?.unitId) searchParams.set('unitId', params.unitId);
  if (params?.propertyId) searchParams.set('propertyId', params.propertyId);
  if (params?.portfolioId) searchParams.set('portfolioId', params.portfolioId);
  if (params?.participantIdentityId) searchParams.set('participantIdentityId', params.participantIdentityId);

  const queryString = searchParams.toString();
  const endpoint = queryString ? `/landlord/api/chat/conversations?${queryString}` : '/landlord/api/chat/conversations';
  return apiFetch<{ conversations: ChatConversation[] }>(endpoint);
}

/**
 * Get a conversation by ID
 */
export async function getConversation(id: string): Promise<ChatConversation> {
  return apiFetch<ChatConversation>(`/landlord/api/chat/conversations/${id}`);
}

/**
 * Create a new conversation
 */
export async function createConversation(data: CreateConversationInput): Promise<ChatConversation> {
  return apiFetch<ChatConversation>('/landlord/api/chat/conversations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * List messages in a conversation
 */
export async function listMessages(
  conversationId: string,
  params?: MessageListParams
): Promise<{ messages: ChatMessage[]; nextCursor: string | null; hasMore: boolean }> {
  const searchParams = new URLSearchParams();
  if (params?.cursor) searchParams.set('cursor', params.cursor);
  if (params?.limit) searchParams.set('limit', String(params.limit));

  const queryString = searchParams.toString();
  const endpoint = queryString
    ? `/landlord/api/chat/conversations/${conversationId}/messages?${queryString}`
    : `/landlord/api/chat/conversations/${conversationId}/messages`;

  return apiFetch<{ messages: ChatMessage[]; nextCursor: string | null; hasMore: boolean }>(endpoint);
}

/**
 * Send a message in a conversation
 */
export async function sendMessage(conversationId: string, data: CreateMessageInput): Promise<{ message: ChatMessage }> {
  return apiFetch<{ message: ChatMessage }>(`/landlord/api/chat/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Mark conversation as read
 */
export async function markConversationRead(conversationId: string, data: MarkReadInput): Promise<void> {
  await apiFetch<void>(`/landlord/api/chat/conversations/${conversationId}/read`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get read receipts for a conversation
 */
export async function getConversationReadReceipts(conversationId: string): Promise<{ readReceipts: ChatReadReceipt[] }> {
  return apiFetch<{ readReceipts: ChatReadReceipt[] }>(`/landlord/api/chat/conversations/${conversationId}/read-receipts`);
}

/**
 * Get Centrifugo connection token
 */
export async function getCentrifugoToken(): Promise<{ token: string }> {
  return apiFetch<{ token: string }>('/landlord/api/chat/centrifugo/token', {
    method: 'POST',
  });
}

/**
 * Upload chat attachment
 */
export async function uploadChatAttachment(file: File): Promise<ChatAttachment> {
  const formData = new FormData();
  formData.append('file', await readFileIntoMemory(file));

  return apiFetch<ChatAttachment>('/landlord/api/chat/attachments/upload', {
    method: 'POST',
    body: formData,
    headers: {},
  });
}

/**
 * Download chat attachment (returns raw Response for streaming)
 */
export async function downloadChatAttachment(id: string): Promise<Response> {
  return fetch(`/landlord/api/chat/attachments/${id}/download`, {
    credentials: 'include',
  });
}

/**
 * Export chat conversation
 */
export async function exportChatConversation(conversationId: string): Promise<Response> {
  return fetch(`/landlord/api/chat/conversations/${conversationId}/export`, {
    method: 'POST',
    credentials: 'include',
  });
}
